import { Component, For, Show, createMemo, createEffect } from "solid-js";
import type { ProposedActionId } from "../../api/types";
import type { ActionGroup, GroupIcon } from "./groupActions";
import { ActionDiffCard } from "./ActionDiffCard";

const iconPaths: Record<GroupIcon, string> = {
  create: "M12 4v16m8-8H4",
  update:
    "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z",
  delete:
    "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16",
  status: "M13 7l5 5m0 0l-5 5m5-5H6",
  document:
    "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  other: "M4 6h16M4 12h16M4 18h7",
};

const iconClasses: Record<GroupIcon, string> = {
  create: "text-signal-green",
  update: "text-signal-blue",
  delete: "text-signal-red",
  status: "text-signal-amber",
  document: "text-ink-3",
  other: "text-ink-3",
};

export interface ActionGroupSectionProps {
  group: ActionGroup;
  selectedActionIds: Set<ProposedActionId>;
  onToggleGroup: () => void;
  onToggleAction: (id: ProposedActionId) => void;
  canAct: boolean;
  /** When true, show compact layout */
  compact?: boolean;
}

const GroupCheckbox: Component<{
  state: "checked" | "unchecked" | "indeterminate";
  onChange: () => void;
  label: string;
}> = (props) => {
  let ref: HTMLInputElement | undefined;

  createEffect(() => {
    if (ref) ref.indeterminate = props.state === "indeterminate";
  });

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={props.state === "checked"}
      onChange={props.onChange}
      class="w-3.5 h-3.5 accent-ink border-line rounded focus-ring"
      aria-label={`Select all actions in group: ${props.label}`}
    />
  );
};

export const ActionGroupSection: Component<ActionGroupSectionProps> = (
  props,
) => {
  const visibleActions = createMemo(() =>
    props.canAct
      ? props.group.actions.filter((a) => a.status === "pending")
      : props.group.actions,
  );

  const pendingCount = createMemo(
    () => props.group.actions.filter((a) => a.status === "pending").length,
  );

  const selectedInGroup = createMemo(
    () =>
      props.group.actions.filter((a) => props.selectedActionIds.has(a.id))
        .length,
  );

  const groupCheckState = createMemo<"checked" | "unchecked" | "indeterminate">(
    () => {
      const total = pendingCount();
      const sel = selectedInGroup();
      if (sel === 0) return "unchecked";
      if (sel === total) return "checked";
      return "indeterminate";
    },
  );

  return (
    <div class="space-y-1.5">
      {/* Group header */}
      <div class="flex items-center gap-2 px-1">
        {/* Group checkbox */}
        <Show when={props.canAct && pendingCount() > 0}>
          <div class="shrink-0">
            <GroupCheckbox
              state={groupCheckState()}
              onChange={props.onToggleGroup}
              label={props.group.label}
            />
          </div>
        </Show>

        {/* Group icon */}
        <svg
          class={"w-3.5 h-3.5 shrink-0 " + iconClasses[props.group.icon]}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d={iconPaths[props.group.icon]}
          />
        </svg>

        {/* Group label */}
        <span class="text-xs font-medium text-ink-2 truncate flex-1">
          {props.group.label}
        </span>

        {/* Action count badge (hidden for single-action groups) */}
        <Show when={props.group.actions.length > 1}>
          <span class="shrink-0 text-xs text-ink-3 bg-sunken px-1.5 py-0.5 rounded-full">
            {props.group.actions.length}
          </span>
        </Show>

        {/* Partial selection indicator */}
        <Show when={groupCheckState() === "indeterminate"}>
          <span class="shrink-0 text-xs text-signal-amber bg-signal-amber-tint px-1.5 py-0.5 rounded-full">
            partial
          </span>
        </Show>
      </div>

      {/* Individual action cards — per-action checkbox is suppressed for
          single-action groups where the group checkbox already controls it */}
      <div class="ml-4 space-y-1.5">
        <For each={visibleActions()}>
          {(action) => (
            <ActionDiffCard
              action={action}
              selected={props.selectedActionIds.has(action.id)}
              selectable={
                props.canAct &&
                action.status === "pending" &&
                props.group.actions.length > 1
              }
              onToggle={() => props.onToggleAction(action.id)}
              compact={props.compact}
            />
          )}
        </For>
      </div>
    </div>
  );
};

export default ActionGroupSection;
